import { useState } from 'react';
import './StarRating.css';

const STARS = [1, 2, 3, 4, 5];
const STAR_PATH =
  'M12 2.5l2.9 5.9 6.5.95-4.7 4.58 1.11 6.47L12 17.35l-5.81 3.05 1.11-6.47-4.7-4.58 6.5-.95z';

const StarIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d={STAR_PATH} />
  </svg>
);

const StarRow = ({ variant }) => (
  <span className={`star-rating-row star-rating-row--${variant}`}>
    {STARS.map((star) => (
      <StarIcon key={star} className="star-rating-icon" />
    ))}
  </span>
);

/**
 * Read-only star display, or an interactive 1-5 picker when onChange is given.
 *
 * The read-only display overlays a gold row on a grey row and clips the gold
 * one by percentage width, so a 3.4 average renders as a real partial star
 * without per-instance SVG clip-path ids (which would collide across a grid).
 */
const StarRating = ({ value = 0, size = 'md', onChange, label }) => {
  const [hovered, setHovered] = useState(0);
  const interactive = typeof onChange === 'function';

  if (!interactive) {
    const clamped = Math.max(0, Math.min(5, Number(value) || 0));

    return (
      <span
        className={`star-rating star-rating--${size}`}
        role="img"
        aria-label={label || `Rated ${clamped} out of 5`}
      >
        <StarRow variant="track" />
        <span
          className="star-rating-clip"
          style={{ width: `${(clamped / 5) * 100}%` }}
        >
          <StarRow variant="fill" />
        </span>
      </span>
    );
  }

  const shown = hovered || value;

  return (
    <span
      className={`star-rating star-rating--${size} star-rating--interactive`}
      onMouseLeave={() => setHovered(0)}
    >
      {STARS.map((star) => (
        <button
          key={star}
          type="button"
          className={`star-rating-button${shown >= star ? ' is-active' : ''}`}
          aria-label={`${star} star${star > 1 ? 's' : ''}`}
          aria-pressed={value === star}
          onMouseEnter={() => setHovered(star)}
          onFocus={() => setHovered(star)}
          onBlur={() => setHovered(0)}
          onClick={() => onChange(star)}
        >
          <StarIcon className="star-rating-icon" />
        </button>
      ))}
    </span>
  );
};

export default StarRating;
